import * as React from 'react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import ChatMessages from 'v2/components/Shared/ChatMessages';
import Environment from 'v2/components/Shared/Environment';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Webchat from 'v2/components/Shared/Webchat';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { useUserStore } from 'v2/store/User';

import Layout from '@eduzz/ui-layout';

import Affiliations from './Affiliations';
import NoPartner from './NoPartner';
import Notifications from './Notifications';
import ProfileMenu from './ProfileMenu';
import Store from './Store';

const { Topbar } = Layout;

type LogoWrapperProps = {
  className: string;
  children: React.ReactNode;
};

function LogoWrapper({ className, children }: LogoWrapperProps) {
  return (
    <Link className={className} to='/producer/dashboard'>
      {children}
    </Link>
  );
}

function Toolbar() {
  const [ModuleTopbarSearch] = useObservable(() => {
    return eduzzAccountsService.getUserVertical().pipe(
      switchMap(user => {
        return from(
          import('@myeduzz-vertical/root/modules').then(async d => {
            return {
              modules: await d.getModules(user as any),
              verticalUser: user
            };
          })
        );
      }),
      map(({ modules, verticalUser }) => {
        return {
          verticalUser,
          Component: modules.find(m => m.name === 'myeduzz')?.topbarSearch
        };
      })
    );
  }, []);

  const user = useUserStore(({ user }) => user);

  const canSeeSupportChat = user?.is_elite || user?.has_chat_black || user?.is_clube_black;

  const userToolbar = useMemo(() => {
    if (!user) return null;

    return {
      id: user.id,
      name: user.business_name ?? user.full_name,
      email: user.email,
      avatar: user.photo,
      belt: eduzzAccountsService.getBeltById(user.current_elite_level?.id),
      tag: (!user.acceptence_terms ? null : user.is_clube_black ? 'unity' : 'pro') as 'unity' | 'pro' | null,
      isSupport: user.is_support,
      isPartner: user.acceptence_terms,
      supportId: user.support_id,
      isClubeBlack: user.is_clube_black,
      liveHelperToken: user.live_helper_token
    };
  }, [user]);

  if (!user) {
    return null;
  }

  return (
    <Topbar
      currentApplication='orbita'
      disableApps={!user.isPartner()}
      user={userToolbar}
      logoByApplication='myeduzz'
      logoWrapper={LogoWrapper}
    >
      {canSeeSupportChat && <Webchat />}

      {ModuleTopbarSearch?.Component && (
        <React.Suspense fallback={<></>}>
          <ModuleTopbarSearch.Component
            SearchComponent={Topbar.Search}
            React={React}
            user={ModuleTopbarSearch.verticalUser}
            runOnOrbita
          />
        </React.Suspense>
      )}

      {user.isPartner() ? (
        <>
          <Topbar.ModeSwitcher />

          <Store />

          <PermissionHide>
            <Affiliations />
          </PermissionHide>

          <PermissionHide action='myeduzz:meus-afiliados:visualizar'>
            <ChatMessages />
          </PermissionHide>

          <Notifications />
          <Environment />
        </>
      ) : (
        <NoPartner />
      )}

      <ProfileMenu />
    </Topbar>
  );
}

export default React.memo(Toolbar);
